import {
  AddonKey,
  ContactFormAction,
  Currency,
  DefaultAction,
  ExternalUrlAction,
  Frequency,
  LineItem,
  OrderFormAction,
  PricingDisplayOption,
  MarketAction as SDKMarketAction,
  Price as SDKPrice,
  Pricing as SDKPricing,
  Status,
} from '@vendasta/marketplace-packages';
import { CONTACT_SALES, Package as StorePackage } from '@vendasta/store';
import { Updatable, formatBillingFrequency } from '../utils';

export interface Price {
  price: number;
  frequency: string;
  is_starting_price?: boolean;
  setup_fee?: number;
}

export interface Pricing {
  currency: string;
  prices: Price[];
  price?: Price;
}

export interface ResellerItemId {
  app_id: string;
  addon_id?: string;
  quantity: number;
  edition_id?: string;
}

export class MarketAction {
  sdkMarketAction: SDKMarketAction;
  constructor(data: SDKMarketAction | string, url: string) {
    if (typeof data === 'string') {
      if (!data) {
        this.sdkMarketAction = new SDKMarketAction();
        this.sdkMarketAction.default = {} as DefaultAction;
      } else {
        this.sdkMarketAction = new SDKMarketAction();
        switch (data) {
          case 'order_form':
            this.sdkMarketAction.orderForm = {} as OrderFormAction;
            break;
          case 'external_url':
            this.sdkMarketAction.externalUrl = { url: url } as ExternalUrlAction;
            break;
          case 'contact_form':
          default:
            this.sdkMarketAction.contactForm = {} as ContactFormAction;
            break;
        }
      }
    } else {
      this.sdkMarketAction = data;
    }
  }

  get url(): string {
    return this.sdkMarketAction && this.sdkMarketAction.externalUrl && this.sdkMarketAction.externalUrl.url;
  }
}

export class Package implements Updatable {
  constructor(data: any) {
    this.solution_id = data.packageId || data.solution_id;
    this.partner_id = data.partnerId || data.partner_id;
    this.market_id = data.marketId || data.market_id;
    this.name = data.name || '';
    this.icon = data.icon;
    this.status = Package.getStatus(data);
    this.tagline = data.tagline;
    this.header_image_url = data.headerImageUrl || data.header_image_url;
    this.pricing = Package.getPricing(data);
    this.selling_price = data.sellingPrice || data.selling_price; //Deprecated
    this.currency = data.currency; //Deprecated
    this.billing_frequency = data.billingFrequency || data.billing_frequency; //Deprecated
    this.billing_frequency_other = data.billingFrequencyOther || data.billing_frequency_other; //Deprecated
    this.content = data.content;
    this.products = data.products;
    this.hide_product_icons_and_names = data.hideProductIconsAndNames || data.hide_product_icons_and_names;
    this.hide_product_details = data.hideProductDetails || data.hide_product_details;
    this.createdDate = data.created ? new Date(data.created) : null;
    this.updatedDate = data.updated ? new Date(data.updated) : null;
    this.addon_keys = data.addonKeys || data.addon_keys;
    this.product_order = data.productOrder || data.product_order;
    this.line_items = data.line_items;
    const market_action = data.marketAction || data.market_action;
    this.market_action =
      market_action instanceof MarketAction
        ? market_action
        : new MarketAction(data.marketAction || data.market_action, data.market_action_url);
    this.pricingDisplayOption = data.pricingDisplayOption || data.pricing_display_option;
  }

  get isDraft(): boolean {
    return this.status && this.status.toUpperCase() === 'DRAFT';
  }

  get isPublished(): boolean {
    return this.status && this.status.toUpperCase() === 'PUBLISHED';
  }

  get isArchived(): boolean {
    return this.status && this.status.toUpperCase() === 'ARCHIVED';
  }

  get statusLabel(): string {
    if (this.isDraft) {
      return 'Draft';
    }
    if (this.isPublished) {
      return 'Published';
    }
    if (this.isArchived) {
      return 'Archived';
    }
  }

  get marketAction(): SDKMarketAction {
    return this.market_action.sdkMarketAction;
  }

  get formattedWholesalePrice(): string {
    let priceString = '';
    const prices = this.getPriceStrings(this.pricing);
    for (const price of prices) {
      if (priceString.length > 0) {
        priceString += ' + ';
      }
      priceString += price;
    }
    return priceString;
  }

  solution_id: string;
  partner_id: string;
  market_id: string;
  name: string;
  icon: string;
  status: string;
  tagline: string;
  header_image_url: string;
  pricing: Pricing;
  selling_price: number; //Deprecated
  currency: string; //Deprecated
  billing_frequency: string; //Deprecated
  billing_frequency_other: string; //Deprecated
  content: string;
  products: string[];
  addon_keys: AddonKey[];
  product_order: string[]; // order of products in the package
  line_items: ResellerItemId[]; //products inside the package with edition information.
  market_action: MarketAction; // object containing config information for "get it now" button behavior
  // denotes whether the package uses legacy pricing stored on the package, or bundle pricing stored in billing
  usesBillingPricing: boolean;
  pricingDisplayOption: PricingDisplayOption;

  hide_product_icons_and_names: boolean;
  hide_product_details: boolean;
  createdDate: Date;
  updatedDate: Date;

  static getPricing(data: any): Pricing {
    //handle the legacy case
    if (!data.pricing && (data.selling_price || data.billing_frequency)) {
      return {
        currency: data.currency,
        prices: [
          {
            price: data.selling_price ? data.selling_price * 100 : null,
            frequency: data.billing_frequency.toUpperCase(),
            is_starting_price: false,
          },
        ],
      };
    }

    const newPricing: Pricing = { currency: Currency[0], prices: [], price: null };
    if (data.pricing) {
      if (data.pricing.currency) {
        if (typeof data.pricing.currency === 'string') {
          newPricing.currency = data.pricing.currency;
        } else {
          newPricing.currency = Currency[data.pricing.currency];
        }
      }
    }

    if (data.pricing && data.pricing.prices) {
      data.pricing.prices.map((price) => {
        //Frequency[0] is the default enum case, being monthly
        const newPrice: Price = { price: price.price, frequency: Frequency[0] };
        if (price.frequency) {
          if (typeof price.frequency === 'string') {
            newPrice.frequency = price.frequency.toUpperCase();
          } else {
            newPrice.frequency = Frequency[price.frequency].toUpperCase();
          }
        }
        newPrice.is_starting_price = price.isStartingPrice || price.is_starting_price;
        newPricing.prices.push(newPrice);
      });
    }

    if (data.pricing && data.pricing.price) {
      newPricing.price = {
        price: data.pricing.price.price,
        setup_fee: data.pricing.price.setup_fee,
        frequency: data.pricing.price.frequency,
      };
    }

    return newPricing;
  }

  static getStatus(data: any): string {
    if (data.status) {
      if (typeof data.status === 'string') {
        return data.status.toUpperCase();
      } else {
        return Status[data.status];
      }
    } else {
      //default to a package being a draft
      return Status[0];
    }
  }

  static getName(data: any): string {
    if (!data.name) {
      return 'Untitled';
    }
    return data.name;
  }

  convertPricingToSDKPricing(): SDKPricing {
    if (!this.pricing) {
      return null;
    }
    const sdkPricing = new SDKPricing({
      currency: Currency[this.pricing.currency],
    });
    const prices = (this.pricing.prices || []).map(
      (price) =>
        new SDKPrice({
          price: price.price,
          isStartingPrice: price.is_starting_price,
          frequency: Frequency[price.frequency],
        }),
    );
    sdkPricing.prices = prices;

    if (this.pricing.price.frequency.toUpperCase() == 'ONE TIME') {
      this.pricing.price.frequency = 'ONCE';
    }

    const price = !this.pricing.price
      ? null
      : new SDKPrice({
          price: this.pricing.price.price,
          setupFee: this.pricing.price.setup_fee,
          frequency: Frequency[this.pricing.price.frequency.toUpperCase()],
        });
    sdkPricing.price = price;

    return sdkPricing;
  }

  convertProductOrderProductAndAddonKeyListToSDKLineItems(): LineItem[] {
    const allKeys: string[] = [];
    const productMap = new Map<string, LineItem>();

    (this.products || []).forEach((product_id) => {
      allKeys.push(product_id);
      if (!productMap.has(product_id)) {
        productMap.set(
          product_id,
          new LineItem({
            id: product_id,
            quantity: 1,
            editionId: '',
          }),
        );
      }
    });
    (this.line_items || ([] as ResellerItemId[])).forEach((product) => {
      allKeys.push(product.app_id);
      productMap.set(
        product.app_id,
        new LineItem({
          id: product.app_id,
          quantity: 1,
          editionId: product.edition_id,
        }),
      );
    });
    (this.addon_keys || []).forEach((addonKey) => {
      allKeys.push(addonKey.addonId || (addonKey as any).addon_id);
      productMap.set(
        addonKey.addonId || (addonKey as any).addon_id,
        new LineItem({
          id: addonKey.addonId || (addonKey as any).addon_id,
          parentId: addonKey.appId || (addonKey as any).app_id,
          quantity: addonKey.quantity || 1,
        }),
      );
    });

    const productOrder = [].concat(this.product_order).concat(allKeys);
    const lineItems: LineItem[] = [];
    productOrder.forEach((id) => {
      if (!productMap.has(id)) {
        return;
      }

      lineItems.push(productMap.get(id));
      productMap.delete(id);
    });
    return lineItems;
  }

  convertSDKLineItemsToProductListWithEdition(lineItems: LineItem[]): ResellerItemId[] {
    if (!lineItems) {
      return [] as ResellerItemId[];
    }
    const items = lineItems.reduce((resellerItems, lineItem) => {
      if (lineItem.id && !lineItem.parentId) {
        resellerItems.push({
          app_id: lineItem.id,
          addon_id: '',
          quantity: 0,
          edition_id: lineItem.editionId,
        });
      }
      return resellerItems;
    }, [] as ResellerItemId[]);

    return items;
  }

  getPriceStrings(pricing: Pricing): string[] {
    let prices = [];
    if (pricing && pricing.prices && pricing.prices.length > 0) {
      let currencySymbol = '$';
      let formatFunc = this.getDefaultPriceString;
      pricing.prices.sort(this.sortPricesByFrequency);
      switch (pricing.currency) {
        case 'EUR':
          currencySymbol = '\u20AC';
          break;
        case 'GBP':
          currencySymbol = '\u00A3';
          break;
        case 'CHF':
          currencySymbol = 'Fr';
          break;
        case 'CNY':
          currencySymbol = '\u00A5';
          break;
        case 'JPY':
          currencySymbol = '\u00A5';
          break;
        case 'CZK':
          currencySymbol = 'K\u010d';
          formatFunc = this.getCzechPriceString;
          break;
        case 'INR':
          currencySymbol = '\u20B9';
          break;
        case 'KHR':
          currencySymbol = '\u17DB';
          break;
        case 'KRW':
          currencySymbol = '\u20A9';
          break;
        case 'NOK':
          currencySymbol = 'kr';
          break;
        case 'SEK':
          currencySymbol = 'kr';
          break;
        case 'RUB':
          currencySymbol = '\u20BD';
          break;
        case 'TRY':
          currencySymbol = '\u20BA';
          break;
        case 'USD':
          formatFunc = this.getUnitedStatesPriceString;
          break;
        case 'ZAR':
          currencySymbol = 'R';
          break;
        default:
          break;
      }

      let anyContactSales = false;
      const isFree = pricing.prices.every((price) => price.price === 0 || price.price === undefined);
      for (const price of pricing.prices) {
        if (price.price === null || price.price <= CONTACT_SALES) {
          anyContactSales = true;
          break;
        } else if (price.price != null && price.price > 0) {
          prices.push(formatFunc(price, currencySymbol, pricing.currency));
        }
      }

      if (anyContactSales) {
        prices = ['Contact Sales'];
      } else if (prices.length === 0 || isFree) {
        prices = ['Free'];
      }
    } else {
      prices = ['Contact Sales'];
    }
    return prices;
  }

  sortPricesByFrequency(priceA: Price, priceB: Price): number {
    // this map is coded based on frequency values described by the enums described in the marketplace packages vendasta
    // apis and the corresponding translations to strings laced throughout galaxy
    const priceFrequencyComparisonMap: Map<string, Map<string, number>> = new Map<string, Map<string, number>>([
      [
        'MONTHLY',
        new Map([
          ['MONTHLY', 0],
          ['YEARLY', -1],
          ['ONCE', -1],
          ['ONE TIME', -1],
          ['ONE_TIME', -1],
          ['WEEKLY', -1],
        ]),
      ],
      [
        'YEARLY',
        new Map([
          ['MONTHLY', 1],
          ['YEARLY', 0],
          ['ONCE', -1],
          ['ONE TIME', -1],
          ['ONE_TIME', -1],
          ['WEEKLY', -1],
        ]),
      ],
      [
        'ONCE',
        new Map([
          ['MONTHLY', 1],
          ['YEARLY', 1],
          ['ONCE', 0],
          ['ONE TIME', 0],
          ['ONE_TIME', 0],
          ['WEEKLY', 0],
        ]),
      ],
      [
        'ONE TIME',
        new Map([
          ['MONTHLY', 1],
          ['YEARLY', 1],
          ['ONCE', 0],
          ['ONE TIME', 0],
          ['ONE_TIME', 0],
          ['WEEKLY', 0],
        ]),
      ],
      [
        'ONE_TIME',
        new Map([
          ['MONTHLY', 1],
          ['YEARLY', 1],
          ['ONCE', 0],
          ['ONE TIME', 0],
          ['ONE_TIME', 0],
          ['WEEKLY', 0],
        ]),
      ],
      [
        'WEEKLY',
        new Map([
          ['MONTHLY', 1],
          ['YEARLY', 1],
          ['ONCE', 1],
          ['ONE TIME', 1],
          ['ONE_TIME', 1],
          ['WEEKLY', 0],
        ]),
      ],
    ]);

    if (priceFrequencyComparisonMap.get(priceA.frequency.toUpperCase()) === undefined) {
      return 0;
    }

    const compareVal = priceFrequencyComparisonMap
      .get(priceA.frequency.toUpperCase())
      .get(priceB.frequency.toUpperCase());
    return compareVal ? compareVal : 0;
  }

  getDefaultPriceString(price: Price, currencySymbol: string, currencyStr: string): string {
    const frequencyStr = price.frequency ? ' ' + formatBillingFrequency(price.frequency) : '';
    const roundedPrice = (price.price / 100.0).toFixed(2);
    const startingAtPrefix = price.is_starting_price ? 'Starting at ' : '';
    const priceStr = `${startingAtPrefix}${currencySymbol}${roundedPrice} ${currencyStr} ${frequencyStr}`;
    return priceStr.trim().replace(/\s+/g, ' ');
  }

  getUnitedStatesPriceString(price: Price, currencySymbol: string, _currencyStr: string): string {
    const frequencyStr = price.frequency ? ' ' + formatBillingFrequency(price.frequency) : '';
    const roundedPrice = (price.price / 100.0).toFixed(2);
    const startingAtPrefix = price.is_starting_price ? 'Starting at ' : '';
    const priceStr = `${startingAtPrefix}${currencySymbol}${roundedPrice} ${frequencyStr}`;
    return priceStr.trim().replace(/\s+/g, ' ');
  }

  getCzechPriceString(price: Price, currencySymbol: string, _currencyStr: string): string {
    const frequencyStr = price.frequency ? ' ' + formatBillingFrequency(price.frequency) : '';
    const roundedPrice = (price.price / 100.0).toFixed(2).replace('.00', '').replace('.', ',');
    const startingAtPrefix = price.is_starting_price ? 'Starting at ' : '';
    const priceStr = `${startingAtPrefix}${roundedPrice} ${currencySymbol} exc. VAT ${frequencyStr}`;
    return priceStr.trim().replace(/\s+/g, ' ');
  }

  convertToStorePackage(): StorePackage {
    const pkg = new StorePackage();

    pkg.solution_id = this.solution_id;
    pkg.partner_id = this.partner_id;
    pkg.market_id = this.market_id;
    pkg.name = this.name;
    pkg.icon = this.icon;
    pkg.status = this.status;
    pkg.tagline = this.tagline;
    pkg.header_image_url = this.header_image_url;
    pkg.content = this.content;
    pkg.products = this.products;
    pkg.addon_keys = this.addon_keys;
    pkg.hide_product_icons_and_names = this.hide_product_icons_and_names;
    pkg.hide_product_details = this.hide_product_details;
    pkg.createdDate = this.createdDate;
    pkg.updatedDate = this.updatedDate;
    pkg.productOrder = this.product_order;
    pkg.pricing = this.pricing;
    return pkg;
  }
}
